import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';

// Kleines Datenmodell für ein Feld
interface Cell {
  bomb: boolean;
  open: boolean;
  flag: 0 | 1 | 2; // 0=none, 1=flag, 2=question
  n: number; // Nachbarbomben
  misflag?: boolean; // wurde als Bombe markiert, ist aber keine (nur bei GameOver)
}

@Component({
  selector: 'app-minesweeper',
  standalone: true,
  imports: [CommonModule, FormsModule],
  styleUrls: ['./minesweeper.component.scss'],
  template: `
    <div class="ms-root win95-ms">
      <div class="wrap">
        <div class="topbar" role="region" aria-label="Minesweeper header">
          <div class="controls">
            <button class="face" (click)="newGame()" [attr.title]="'New Game (F2)'">
              {{ faceEmoji }}
            </button>
            <div class="stat" title="Time">
              <span class="label">Time</span>
              <span class="value">{{ timeString }}</span>
            </div>
            <div class="stat" title="Mines left">
              <span class="label">Mines</span>
              <span class="value">{{ bombsLeftPad }}</span>
            </div>
            <div class="stat" title="Moves">
              <span class="label">Moves</span>
              <span class="value">{{ moves }}</span>
            </div>
          </div>
          <div class="controls">
            <select
              [(ngModel)]="difficulty"
              (change)="onDifficultyChange()"
              aria-label="Difficulty"
            >
              <option value="beginner">Beginner 8×8, 10 mines</option>
              <option value="intermediate">Intermediate 16×16, 40 mines</option>
              <option value="expert">Expert 30×16, 99 mines</option>
              <option value="custom">Custom…</option>
            </select>
            <label
              >Width
              <input
                type="number"
                [(ngModel)]="customW"
                min="8"
                max="32"
                (change)="applyCustom(false)"
                style="width:72px"
            /></label>
            <label
              >Height
              <input
                type="number"
                [(ngModel)]="customH"
                min="8"
                max="24"
                (change)="applyCustom(false)"
                style="width:72px"
            /></label>
            <label
              >Mines
              <input
                type="number"
                [(ngModel)]="customB"
                min="1"
                (change)="applyCustom(false)"
                style="width:80px"
            /></label>
            <label><input type="checkbox" [(ngModel)]="useQuestionMarks" /> Question marks</label>
            <label><input type="checkbox" [(ngModel)]="safeFirstClick" /> Safe first click</label>
            <button class="primary" (click)="onStartClick()">Start</button>
          </div>
        </div>
        <div class="toolbar legend">
          @if (isMobile) { Tap: open · Long‑press: flag · Tap number (with flags): open neighbors }
          @else { Left: open · Right: flag · Middle/Chord: open neighbors · F2: new }
        </div>

        <div class="grid">
          <div
            class="board"
            [style.--cell]="cellSize"
            [style.gridTemplateColumns]="'repeat(' + W + ', ' + cellSize + ')'"
            (contextmenu)="$event.preventDefault()"
          >
            <div
              *ngFor="let c of cells; let i = index; trackBy: trackByIndex"
              class="cell"
              [class.open]="c.open"
              [class.bomb]="c.open && (c.bomb || i === explodedIndex)"
              [class.flag]="!c.open && c.flag === 1"
              [class.question]="!c.open && c.flag === 2"
              [class.n1]="c.open && !c.bomb && c.n === 1"
              [class.n2]="c.open && !c.bomb && c.n === 2"
              [class.n3]="c.open && !c.bomb && c.n === 3"
              [class.n4]="c.open && !c.bomb && c.n === 4"
              [class.n5]="c.open && !c.bomb && c.n === 5"
              [class.n6]="c.open && !c.bomb && c.n === 6"
              [class.n7]="c.open && !c.bomb && c.n === 7"
              [class.n8]="c.open && !c.bomb && c.n === 8"
              role="button"
              [attr.aria-label]="ariaLabelFor(i)"
              (mousedown)="onCellMouseDown(i, $event)"
              (mouseup)="onCellMouseUp(i, $event)"
              (mouseleave)="onCellMouseLeave()"
              (touchstart)="onCellTouchStart(i, $event)"
              (touchend)="onCellTouchEnd(i, $event)"
              (touchcancel)="onCellTouchCancel()"
            >
              {{ cellText(i) }}
            </div>
          </div>
        </div>

        <div class="toast" [class.bad]="toastBad" aria-live="polite">{{ toast }}</div>
        @if (isMobile) {
        <p class="legend">
          Tip: Tap to open; long‑press to place a flag (or question mark). “Safe first click” clears
          around your first tap. Have fun — and avoid jumping on bombs! 💣
        </p>
        } @else {
        <p class="legend">
          Tip: The first click clears the surrounding area (if “Safe first click” is enabled). Have
          fun — and avoid jumping on bombs! 💣
        </p>
        }
      </div>
    </div>
  `,
})
export class MinesweeperComponent implements OnInit, OnDestroy {
  // ======= Spiel-Status =======
  W = 8;
  H = 8;
  B = 10; // Breite, Höhe, Minen
  readonly MAX_W = 32;
  readonly MAX_H = 24;
  cells: Cell[] = [];
  firstClickDone = false;
  useQuestionMarks = true;
  safeFirstClick = true;
  moves = 0;
  flags = 0;
  opened = 0;
  dead = false;
  won = false;
  timer: ReturnType<typeof setInterval> | null = null;
  seconds = 0;
  explodedIndex: number | null = null;

  // UI
  difficulty: 'beginner' | 'intermediate' | 'expert' | 'custom' = 'beginner';
  customW = 8;
  customH = 8;
  customB = 10;
  faceEmoji = 'Restart';
  timeString = '000';
  cellSize = '32px';
  toast = '';
  toastBad = false;

  // ======= Lifecycle =======
  ngOnInit(): void {
    this.newGame();
    this.updateCellSize();
  }
  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = 0;
    }
  }

  // ======= Host Events =======
  @HostListener('window:resize') onResize() {
    this.updateCellSize();
  }
  @HostListener('document:keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    const legacyKeyCode = (e as KeyboardEvent & { keyCode?: number }).keyCode;
    if (e.key === 'F2' || legacyKeyCode === 113) {
      e.preventDefault();
      this.newGame();
    }
  }

  get isMobile(): boolean {
    return (
      /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ||
      'ontouchstart' in window ||
      window.innerWidth < 768
    );
  }

  // ======= Hilfsfunktionen =======
  idx(x: number, y: number) {
    return x + y * this.W;
  }
  inBounds(x: number, y: number) {
    return x >= 0 && x < this.W && y >= 0 && y < this.H;
  }
  neighbors(x: number, y: number, fn: (nx: number, ny: number) => void) {
    for (let dx = -1; dx <= 1; dx++)
      for (let dy = -1; dy <= 1; dy++) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx,
          ny = y + dy;
        if (this.inBounds(nx, ny)) fn(nx, ny);
      }
  }
  pad3(n: number) {
    return String(n).padStart(3, '0').slice(-3);
  }
  clampInt(v: number, min: number, max: number) {
    return Math.max(min, Math.min(max, Math.round(v)));
  }
  maxLegalBombsFrom(w: number, h: number) {
    return Math.floor((w * h) / 3);
  }
  updateCellSize() {
    const vw = Math.min(window.innerWidth * 0.95, 1100) - 40;
    const vh = window.innerHeight * 0.7;
    const cw = Math.floor(vw / this.W);
    const ch = Math.floor(vh / this.H);
    const s = this.clampInt(Math.min(cw, ch), 22, 40);
    this.cellSize = s + 'px';
  }
  message(msg: string, bad = false) {
    this.toast = msg;
    this.toastBad = !!bad;
    if (msg) {
      setTimeout(() => {
        if (this.toast === msg) this.toast = '';
      }, 4000);
    }
  }

  bombsLeft() {
    return Math.max(0, this.B - this.flags);
  }
  get bombsLeftPad() {
    return this.pad3(this.bombsLeft());
  }

  ariaLabelFor(i: number) {
    return `Cell ${(i % this.W) + 1},${Math.floor(i / this.W) + 1}`;
  }

  trackByIndex(index: number) {
    return index;
  }

  // ======= Board-Erzeugung =======
  newGame() {
    this.setDifficultyFromUI();
    this.firstClickDone = false;
    this.dead = false;
    this.won = false;
    this.explodedIndex = null;
    this.moves = 0;
    this.seconds = 0;
    this.opened = 0;
    this.flags = 0;
    this.updateStats();
    this.faceEmoji = 'Restart';
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.timeString = '000';
    this.cells = Array.from(
      { length: this.W * this.H },
      () => ({ bomb: false, open: false, flag: 0, n: 0 } as Cell)
    );
    this.updateCellSize();
  }

  setDifficultyFromUI() {
    const v = this.difficulty;
    if (v === 'beginner') {
      this.W = 8;
      this.H = 8;
      this.B = 10;
      this.customW = 8;
      this.customH = 8;
      this.customB = 10;
    } else if (v === 'intermediate') {
      this.W = 16;
      this.H = 16;
      this.B = 40;
      this.customW = 16;
      this.customH = 16;
      this.customB = 40;
    } else if (v === 'expert') {
      this.W = 30;
      this.H = 16;
      this.B = 99;
      this.customW = 30;
      this.customH = 16;
      this.customB = 99;
    } else {
      // custom
      const maxB = Math.min(this.maxLegalBombsFrom(this.customW, this.customH), 999);
      this.customW = this.clampInt(this.customW || 8, 8, this.MAX_W);
      this.customH = this.clampInt(this.customH || 8, 8, this.MAX_H);
      this.customB = this.clampInt(this.customB || 10, 1, maxB);
      this.W = this.customW;
      this.H = this.customH;
      this.B = this.customB;
    }
  }

  onDifficultyChange() {
    if (this.difficulty !== 'custom') {
      this.newGame();
    } else {
      this.applyCustom(false);
    }
  }

  applyCustom(startNow = true) {
    this.difficulty = 'custom';
    const w = this.clampInt(this.customW || 8, 8, this.MAX_W);
    const h = this.clampInt(this.customH || 8, 8, this.MAX_H);
    const maxB = this.maxLegalBombsFrom(w, h);
    const b = this.clampInt(this.customB || 10, 1, maxB);
    if ((this.customB || 10) > maxB) {
      this.customB = maxB;
      this.message(`Too many mines! Maximum ${maxB} allowed (1/3 of cells).`, true);
    }
    this.customW = w;
    this.customH = h;
    this.customB = b;
    this.W = w;
    this.H = h;
    this.B = b;
    this.updateCellSize();
    if (startNow) this.newGame();
  }

  onStartClick() {
    if (this.difficulty === 'custom') this.applyCustom(true);
    else this.newGame();
  }

  // ======= Interaktionen =======
  isPressing = false;
  private touchPressIndex: number | null = null;
  private touchLongPressTimer: number | null = null;
  private touchDidLongPress = false;
  startTimer() {
    if (this.timer) return;
    this.seconds = 0;
    this.timeString = '000';
    this.timer = setInterval(() => {
      this.seconds = Math.min(999, this.seconds + 1);
      this.timeString = this.pad3(this.seconds);
    }, 1000);
  }

  onCellMouseDown(i: number, e: MouseEvent) {
    if (this.dead || this.won) return;
    this.isPressing = true;
    this.faceEmoji = '😮';
    e.preventDefault();
  }
  onCellMouseLeave() {
    if (this.isPressing) this.isPressing = false;
  }
  onCellMouseUp(i: number, e: MouseEvent) {
    if (this.dead || this.won) return;
    this.isPressing = false;
    this.faceEmoji = 'Restart';
    const x = i % this.W,
      y = Math.floor(i / this.W);
    if (e.button === 0) {
      this.reveal(x, y);
    } else if (e.button === 2) {
      this.toggleFlag(x, y);
    } else if (e.button === 1) {
      this.chordOpen(x, y);
    }
  }

  // Mobile touch: long-press (>500ms) flags; short tap reveals
  onCellTouchStart(i: number, e: TouchEvent) {
    if (!this.isMobile || this.dead || this.won) return;
    e.preventDefault();
    this.isPressing = true;
    this.faceEmoji = '😮';
    this.touchDidLongPress = false;
    this.touchPressIndex = i;
    const x = i % this.W,
      y = Math.floor(i / this.W);
    // schedule long press action
    this.touchLongPressTimer = window.setTimeout(() => {
      // still pressing and same index
      if (this.isPressing && this.touchPressIndex === i) {
        this.toggleFlag(x, y);
        const vib = (navigator as unknown as { vibrate?: (pattern: number | number[]) => boolean })
          .vibrate;
        if (typeof vib === 'function') {
          vib(20);
        }
        this.touchDidLongPress = true;
        this.isPressing = false;
        this.faceEmoji = 'Restart';
      }
    }, 500);
  }

  onCellTouchEnd(i: number, e: TouchEvent) {
    if (!this.isMobile || this.dead || this.won) return;
    e.preventDefault();
    if (this.touchLongPressTimer) {
      clearTimeout(this.touchLongPressTimer);
      this.touchLongPressTimer = null;
    }
    const x = i % this.W,
      y = Math.floor(i / this.W);
    if (!this.touchDidLongPress) {
      // treat as tap
      this.reveal(x, y);
    }
    this.isPressing = false;
    this.faceEmoji = 'Restart';
    this.touchPressIndex = null;
    this.touchDidLongPress = false;
  }

  onCellTouchCancel() {
    if (!this.isMobile) return;
    if (this.touchLongPressTimer) {
      clearTimeout(this.touchLongPressTimer);
      this.touchLongPressTimer = null;
    }
    this.isPressing = false;
    this.faceEmoji = 'Restart';
    this.touchPressIndex = null;
    this.touchDidLongPress = false;
  }

  placeBombs(excludeX: number, excludeY: number) {
    let left = this.B;
    const forbidden = new Set<number>();
    if (this.safeFirstClick) {
      for (let dx = -1; dx <= 1; dx++)
        for (let dy = -1; dy <= 1; dy++) {
          const nx = excludeX + dx,
            ny = excludeY + dy;
          if (this.inBounds(nx, ny)) forbidden.add(this.idx(nx, ny));
        }
    }
    while (left > 0) {
      const rx = (Math.random() * this.W) | 0;
      const ry = (Math.random() * this.H) | 0;
      const ii = this.idx(rx, ry);
      if (forbidden.has(ii)) continue;
      if (!this.cells[ii].bomb) {
        this.cells[ii].bomb = true;
        left--;
      }
    }
    // Nachbarzahlen berechnen
    for (let y = 0; y < this.H; y++) {
      for (let x = 0; x < this.W; x++) {
        const ii = this.idx(x, y);
        if (this.cells[ii].bomb) continue;
        let n = 0;
        this.neighbors(x, y, (nx, ny) => {
          if (this.cells[this.idx(nx, ny)].bomb) n++;
        });
        this.cells[ii].n = n;
      }
    }
  }

  reveal(x: number, y: number) {
    if (this.dead || this.won) return;
    const i = this.idx(x, y);
    const c = this.cells[i];
    if (c.open || c.flag === 1) return;
    if (!this.firstClickDone) {
      this.placeBombs(x, y);
      this.firstClickDone = true;
      this.startTimer();
    }
    this.moves++;
    this.updateStats();
    if (c.bomb) {
      c.open = true;
      this.renderCell(i);
      this.gameOver(false, i);
      return;
    }
    this.floodOpenFrom(x, y);
    this.checkWin();
  }

  floodOpenFrom(x: number, y: number) {
    const stack: [number, number][] = [[x, y]];
    while (stack.length) {
      const popped = stack?.pop() ?? [0, 0]; // stack kann leer oder undefined sein
      const [cx, cy] = popped;
      const ci = this.idx(cx, cy);
      const cell = this.cells[ci];
      if (cell.open || cell.flag === 1) continue;
      cell.open = true;
      this.opened++;
      this.renderCell(ci);
      if (cell.n === 0) {
        this.neighbors(cx, cy, (nx, ny) => {
          const ni = this.idx(nx, ny);
          if (!this.cells[ni].open && this.cells[ni].flag !== 1) stack.push([nx, ny]);
        });
      }
    }
  }

  toggleFlag(x: number, y: number) {
    if (!this.firstClickDone) this.startTimer();
    const c = this.cells[this.idx(x, y)];
    if (c.open) return;
    if (c.flag === 1) {
      c.flag = this.useQuestionMarks ? 2 : 0;
      this.flags--;
    } else if (c.flag === 2) {
      c.flag = 0;
    } else {
      c.flag = 1;
      this.flags++;
    }
    this.moves++;
    this.updateStats();
    this.renderCell(this.idx(x, y));
  }

  chordOpen(x: number, y: number) {
    const c = this.cells[this.idx(x, y)];
    if (!c.open) return;
    let fl = 0;
    this.neighbors(x, y, (nx, ny) => {
      if (this.cells[this.idx(nx, ny)].flag === 1) fl++;
    });
    if (fl === c.n) {
      this.neighbors(x, y, (nx, ny) => {
        if (!this.cells[this.idx(nx, ny)].open && this.cells[this.idx(nx, ny)].flag !== 1)
          this.reveal(nx, ny);
      });
    }
  }

  gameOver(win: boolean, boomIndex: number | null = null) {
    this.dead = !win;
    this.won = !!win;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (win) {
      this.faceEmoji = '😎';
      this.message('You win! 🎉');
      for (let i = 0; i < this.cells.length; i++) {
        const c = this.cells[i];
        if (!c.open && !c.flag) {
          c.flag = 1;
          this.flags++;
          this.renderCell(i);
        }
      }
    } else {
      this.faceEmoji = '💀';
      this.message('Boom! Try again.', true);
      this.explodedIndex = boomIndex;
      for (let i = 0; i < this.cells.length; i++) {
        const c = this.cells[i];
        if (c.bomb && i !== boomIndex) {
          c.open = true;
        } else if (!c.bomb && c.flag === 1) {
          c.open = true;
          c.misflag = true;
        }
      }
    }
    this.updateStats();
  }

  checkWin() {
    if (this.opened === this.W * this.H - this.B) {
      this.gameOver(true);
    }
  }

  // ======= Rendering (leichtgewichtig, da Angular Template verwendet) =======
  renderCell(i: number) {
    console.debug('renderCell', i);
  }
  cellText(i: number) {
    const c = this.cells[i];
    if (c.open) {
      if (i === this.explodedIndex) return '💥';
      if (c.bomb) return '💣';
      if (c.misflag) return '❌';
      return c.n > 0 ? String(c.n) : '';
    }
    if (c.flag === 1) return '🚩';
    if (c.flag === 2) return '?';
    return '';
  }

  updateStats() {
    this.timeString = this.pad3(this.seconds);
  }
}
